import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { interfaceVersion as interfaceVersionApi } from "../lib/endpoints";

export const UI_VERSION_STORAGE_KEY = "tride-ui";

export const UI_VERSIONS = ["v1", "v2"];

/** What everyone gets until the server says otherwise — and what SSR renders. */
export const DEFAULT_UI_VERSION = "v1";

/**
 * Which interface the app is rendered in: "v1" or "v2".
 *
 * Shaped like neither of its neighbours, for a reason each. The theme is a device setting that
 * nothing server-side knows about; the language is an account setting read from `/me`. The
 * interface version is a *deployment* setting — one value for everybody, flipped by an admin
 * (plan §0, decision 1) — so the truth is a public endpoint rather than the account, and it has to
 * be consulted even when there is no account, because the login page follows it too.
 *
 * localStorage holds a copy for the one thing that cannot wait for a round trip: the first paint.
 * The no-flash script in gatsby-ssr stamps `<html data-ui>` from it before React exists, and this
 * provider then reconciles to the server's answer. The server wins whenever it answers; when it
 * cannot (offline, or a backend that predates the endpoint and 404s) the local copy stands, which
 * is the least surprising thing to keep showing.
 *
 * Mirrored onto `<html data-ui>` rather than kept in React alone so that `styles/v2.css`, which is
 * scoped entirely under that attribute, switches without any element subscribing to this context.
 */
const UiVersionContext = createContext({
  version: DEFAULT_UI_VERSION,
  isV2: false,
  known: true,
  setVersion: () => {},
});

/** How long a first visit waits for the server's answer before rendering on the default anyway. */
const KNOWN_TIMEOUT_MS = 4000;

const valid = (v) => UI_VERSIONS.includes(v);

/** The stored copy, or null when there is none (a first visit, or storage disabled). */
const storedVersion = () => {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(UI_VERSION_STORAGE_KEY);
    return valid(v) ? v : null;
  } catch (_) {
    // Private browsing / storage disabled — the server's answer still arrives a moment later.
    return null;
  }
};

const storeVersion = (v) => {
  try {
    window.localStorage.setItem(UI_VERSION_STORAGE_KEY, v);
  } catch (_) { /* the choice still holds for this page; the next load asks the server again */ }
};

export const UiVersionProvider = ({ children }) => {
  // Start on the default for SSR and the first paint so the server-rendered markup matches what
  // React expects; the no-flash script has already stamped the attribute, and the effects below
  // reconcile state to it. Same dance ThemeProvider and LocaleProvider do.
  const [version, setVersionState] = useState(DEFAULT_UI_VERSION);
  const { user } = useAuth();
  // Only the identity matters for refetching — `user` itself is replaced on every `refresh()`,
  // which would otherwise turn each profile save into a request here.
  const userId = user?.id ?? null;
  // Numbers every fetch, so only the newest answer is applied — a focus refetch can race the mount
  // fetch, and neither may overwrite a `setVersion` that happened while it was in flight.
  const seq = useRef(0);

  /*
   * `known`: a version worth rendering a route view in — the stored copy, or the server's answer (or
   * its failure, or a timeout) on a first visit. Until then the default "v1" is only a guess, and a
   * route rendered on it mounts the v1 view for a moment and sends its requests: on the points-system
   * page that was two policy reads refused with 403 for a colleague in v2.
   */
  const [known, setKnown] = useState(false);

  const apply = useCallback((next) => {
    if (!valid(next)) return;
    setVersionState(next);
    storeVersion(next);
    /*
     * Stamped here as well as in the effect below, because the two are not the same instant: the
     * server's answer flips `known` and the version in one render, so the v2 views mount in that
     * commit while the attribute all of v2.css hangs off was still "v1" until the effect ran after
     * the paint — about 140 ms of v2 markup with none of its styling on a device with nothing stored.
     */
    if (typeof document !== "undefined") document.documentElement.dataset.ui = next;
  }, []);

  // False until the stored copy has been read. The mirror effect below waits for it: on the very
  // first commit `version` is still the SSR default, and stamping that would strip a "v2" the
  // no-flash script had already painted — one frame of v1 colours on every reload.
  const [reconciled, setReconciled] = useState(false);

  useEffect(() => {
    const stored = storedVersion();
    setVersionState(stored || DEFAULT_UI_VERSION);
    if (typeof document !== "undefined") document.documentElement.dataset.ui = stored || DEFAULT_UI_VERSION;
    setReconciled(true);
    if (stored) { setKnown(true); return undefined; }
    const timer = window.setTimeout(() => setKnown(true), KNOWN_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  /*
   * Ask the server on mount, whenever the window regains focus, and whenever the signed-in person
   * changes.
   *
   * Focus is what makes an admin's switch reach a second browser without a reload or a push
   * channel: nobody is looking at a tab that is in the background, and the moment they come back
   * to it is the moment it is worth being right. The user dependency covers signing in and out,
   * which is when a session-bound proxy or a stale cache is most likely to have answered wrongly.
   *
   * `cancelled` stops an answer landing after unmount; `seq` stops a stale one landing at all.
   */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let cancelled = false;
    const fetchVersion = () => {
      const mine = ++seq.current;
      interfaceVersionApi
        .get()
        .then((v) => {
          if (!cancelled && mine === seq.current) apply(v);
        })
        .catch(() => { /* keep the local value: offline, or a backend without the endpoint yet */ })
        .finally(() => { if (!cancelled) setKnown(true); });
    };
    fetchVersion();
    window.addEventListener("focus", fetchVersion);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", fetchVersion);
    };
  }, [userId, apply]);

  useEffect(() => {
    if (!reconciled || typeof document === "undefined") return;
    document.documentElement.dataset.ui = version;
  }, [version, reconciled]);

  /**
   * Switch locally and immediately.
   *
   * Persisting it is the caller's job — Profile owns the PUT, the spinner and the rollback, exactly
   * as it does for the language — so this is called only after the server has accepted the change.
   * That keeps this context free of any opinion about failure, and means a rejected PUT never shows
   * everyone's interface flickering to a version that was not saved.
   */
  const setVersion = useCallback((next) => {
    // Invalidate any GET still in flight: it was asked before the PUT and would put the old
    // version back a moment after the admin saw the new one appear.
    seq.current += 1;
    apply(next);
    setKnown(true);
  }, [apply]);

  const value = useMemo(
    () => ({ version, isV2: version === "v2", known, setVersion }),
    [version, known, setVersion]
  );

  return <UiVersionContext.Provider value={value}>{children}</UiVersionContext.Provider>;
};

export const useUiVersionContext = () => useContext(UiVersionContext);
